import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { EInvoiceAvailableRuleSet } from '../../../../data/models/EInvoice'
import { ExtendedEInvoiceValidationResult } from '../../data'

type Props = {
  title: string
  invoiceFormat?: string
  invoiceVersion?: string | null
  availableRuleSets?: EInvoiceAvailableRuleSet[] | null
  results?: ExtendedEInvoiceValidationResult[] | null
  visible: boolean
  setVisible: (visible: boolean) => void
  showSuccessColumn?: boolean
}

export const RuleSetsInfoDialog: React.FC<Props> = ({
  title,
  invoiceFormat,
  invoiceVersion,
  availableRuleSets,
  results,
  visible,
  setVisible,
  showSuccessColumn = false,
}) => {
  const { t } = useTranslation()

  // Group conformance results by RuleSet
  const ruleSetSummary = getRuleSetSummary(availableRuleSets, results)

  // Calculate totals
  const totalErrors = ruleSetSummary.reduce((sum, row) => sum + row.Errors, 0)
  const totalWarnings = ruleSetSummary.reduce(
    (sum, row) => sum + row.Warning,
    0,
  )
  const totalSuccess = ruleSetSummary.reduce((sum, row) => sum + row.Success, 0)
  const total = totalErrors + totalWarnings + totalSuccess

  const dialogFooter = (
    <Button
      label={t('eInvoiceDetails.common.ruleSetsInfoDialog.close.label')}
      icon="pi pi-times"
      onClick={() => setVisible(false)}
      className="p-button-text"
    />
  )

  const createCircleIcon = (
    icon: string,
    title: string,
    level: 'error' | 'warning' | 'success',
  ) => {
    return (
      <span
        title={title}
        className={classNames(
          'border-circle p-2 flex justify-content-center align-items-center',
          {
            'bg-green-500': level === 'success',
            'bg-yellow-500': level === 'warning',
            'bg-red-500': level === 'error',
          },
        )}
      >
        <i className={`pi ${icon} text-white`}></i>
      </span>
    )
  }

  const tableHeader = (
    <h3 className="m-0">
      {t('eInvoiceDetails.common.ruleSetsInfoDialog.table.title')}
    </h3>
  )

  const errorsHeader = createCircleIcon(
    'pi-times-circle',
    t('eInvoiceDetails.common.ruleSetsInfoDialog.table.columns.errors.header'),
    'error',
  )

  const warningsHeader = createCircleIcon(
    'pi-exclamation-triangle',
    t(
      'eInvoiceDetails.common.ruleSetsInfoDialog.table.columns.warnings.header',
    ),
    'warning',
  )

  const successHeader = createCircleIcon(
    'pi-check-circle',
    t('eInvoiceDetails.common.ruleSetsInfoDialog.table.columns.success.header'),
    'success',
  )

  const resultBodyTemplate = (count?: number) => (
    <span
      className={classNames('text-400', {
        'font-bold text-color': count && count > 0,
      })}
    >
      {count}
    </span>
  )

  return (
    <Dialog
      header={title}
      visible={visible}
      style={{ width: '50vw' }}
      onHide={() => setVisible(false)}
      footer={dialogFooter}
    >
      <div className="grid mb-4">
        {/* Detected document type */}
        <div className="field col-12 lg:col-6">
          <label htmlFor="invoiceFormat" className="text-sm font-bold">
            {t(
              'eInvoiceDetails.common.ruleSetsInfoDialog.detectedDocumentType',
            )}
          </label>
          <InputText
            id="invoiceFormat"
            value={invoiceFormat}
            className="w-full"
            disabled
          />
        </div>

        {/* Detected document version */}
        <div className="field col-12 lg:col-6">
          <label htmlFor="invoiceVersion" className="text-sm font-bold">
            {t(
              'eInvoiceDetails.common.ruleSetsInfoDialog.detectedDocumentVersion',
            )}
          </label>
          <InputText
            id="invoiceVersion"
            value={invoiceVersion || ''}
            className="w-full"
            disabled
          />
        </div>
      </div>

      <div>
        <DataTable
          value={ruleSetSummary}
          className="w-full"
          header={tableHeader}
          rowHover
          sortField="RuleSet"
          sortOrder={1}
        >
          <Column
            field="RuleSet"
            header={t(
              'eInvoiceDetails.common.ruleSetsInfoDialog.table.columns.validationRules.header',
            )}
            footer={t(
              'eInvoiceDetails.common.ruleSetsInfoDialog.table.columns.validationRules.footer',
              { total: total },
            )}
          />
          <Column
            field="Errors"
            header={errorsHeader}
            body={(rowData: RuleSetSummary) =>
              resultBodyTemplate(rowData.Errors)
            }
            footer={totalErrors}
            align="center"
          />
          <Column
            field="Warning"
            header={warningsHeader}
            body={(rowData: RuleSetSummary) =>
              resultBodyTemplate(rowData.Warning)
            }
            footer={totalWarnings}
            align="center"
          />
          {showSuccessColumn && (
            <Column
              field="Success"
              header={successHeader}
              body={(rowData: RuleSetSummary) =>
                resultBodyTemplate(rowData.Success)
              }
              footer={totalSuccess}
              align="center"
            />
          )}
        </DataTable>
      </div>
    </Dialog>
  )
}

interface RuleSetSummary {
  RuleSet: string
  Errors: number
  Warning: number
  Success: number
}

const getRuleSetSummary = (
  availableRuleSets?: EInvoiceAvailableRuleSet[] | null,
  results?: ExtendedEInvoiceValidationResult[] | null,
): RuleSetSummary[] => {
  // Initialize all RuleSets with 0 values
  const ruleSetMap = new Map<string, RuleSetSummary>()

  availableRuleSets?.forEach(ruleSetObj => {
    if (ruleSetObj?.ruleset) {
      const ruleSetKey = ruleSetObj.ruleset.toLowerCase()
      ruleSetMap.set(ruleSetKey, {
        RuleSet: ruleSetObj.ruleset,
        Errors: 0,
        Warning: 0,
        Success: 0,
      })
    }
  })

  // Count the actual results
  results?.forEach(result => {
    const ruleSet = result.RuleSet
    // Create entry only if the RuleSet is in the list and is not null
    if (!ruleSet) {
      return
    }

    const ruleSetKey = ruleSet.toLowerCase()
    if (!ruleSetMap.has(ruleSetKey)) {
      return
    }

    const summary = ruleSetMap.get(ruleSetKey)!

    switch (result.Level) {
      case 'Error':
        summary.Errors++
        break
      case 'Warn':
        summary.Warning++
        break
      case 'Success':
        summary.Success++
        break
    }
  })

  return Array.from(ruleSetMap.values())
}
